import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { useDebouncedCallback } from 'use-debounce'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import Autocomplete from '@mui/material/Autocomplete'
import Avatar from '@mui/material/Avatar'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded'

import TextInput from '@/components/ui/inputs/TextInput'

import DoctorSvg from '@/assets/svg/doctors.svg'
import NurseSvg from '@/assets/svg/nurse.svg'
import PatientSvg from '@/assets/svg/patients.svg'

import { ConsultationParticipantType } from '@/core/models/consultation.model'
import { randomString } from '@/utils/helpers'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { Tooltip } from '@mui/material'

export type Participant = {
  name: string
  surname: string
  email: string
  type: ConsultationParticipantType
  assignToDoctor?: boolean
  skipNotification?: boolean
}

export type InvitationDialogProps = {
  autoHightlight?: boolean
  isSearching?: boolean
  loading?: boolean
  onClose: () => void
  open: boolean
  onRemoveParticipant?: (participant: Participant) => void
  onSelectParticipant?: (participant: Participant) => void
  onSearchParticipant?: (searchTerm: string, participantType: string) => void
  participants: Participant[]
  suggestedParticipants: Participant[]
}

const participantIconMap: Record<string, string> = {
  doctor: DoctorSvg,
  pds: NurseSvg,
  patient: PatientSvg,
}

const schema = yup.object({
  email: yup.string().email(),
})

export function InvitationDialog(props: InvitationDialogProps) {
  const { isSearching = false, onSearchParticipant, open, onClose, onRemoveParticipant, onSelectParticipant, participants, suggestedParticipants } = props
  const { t } = useTranslation()
  const isTooSmall = useIsMobile()

  // intentional for mode onChange, others cases it should be onBlur
  const { control, setValue } = useForm({ defaultValues: { email: '' }, mode: 'onChange', resolver: yupResolver(schema) })

  const [participantTypeTab, setParticipantTypeTab] = useState('doctor')
  const [assignDoctor, setAssignDoctor] = useState(false)
  const [skipNotification, setSkipNotification] = useState(() => localStorage.getItem('inviteSkipNotification') === 'true')

  const suggestedParticipantOptions = useMemo(
    () =>
      suggestedParticipants
        .filter((participant) => participant.type.toLowerCase() === participantTypeTab)
        .map((participant) => ({
          creatable: false,
          label: participant.email,
          value: participant.email,
        })),
    [participantTypeTab, suggestedParticipants]
  )

  const debouncedOnChange = useDebouncedCallback((inputValue: string) => {
    onSearchParticipant?.(inputValue, participantTypeTab)
  }, 300)

  const handleChangeParticipantTypeTab = (_event: React.SyntheticEvent, newValue: string) => {
    setParticipantTypeTab(newValue)
    setAssignDoctor(false)
  }
  const handleChangeParticipantTypeSelect = (event: SelectChangeEvent<string>) => {
    setParticipantTypeTab(event.target.value)
    setAssignDoctor(false)
  }

  const handleRemoveParticipant = (participant: Participant) => () => {
    onRemoveParticipant?.(participant)
  }

  return (
    <Dialog open={open} PaperProps={{ sx: { height: '100%', minWidth: isTooSmall ? '100%' : (theme) => theme.breakpoints.values.md } }} onClose={onClose} scroll="paper">
      <DialogTitle>{t('page.liveConsultation.invitationDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} direction="column">
          {isTooSmall ? (
            <Select
              value={participantTypeTab}
              onChange={handleChangeParticipantTypeSelect}
              fullWidth
            >
              <MenuItem value="doctor">{t('page.liveConsultation.invitationDialog.tab.doctor')}</MenuItem>
              <MenuItem value="pds">{t('page.liveConsultation.invitationDialog.tab.nurse')}</MenuItem>
              <MenuItem value="patient">{t('page.liveConsultation.invitationDialog.tab.patient')}</MenuItem>
            </Select>
          ) : (
            <Tabs
              onChange={handleChangeParticipantTypeTab}
              selectionFollowsFocus
              value={participantTypeTab}
              variant="fullWidth"
            >
              <Tab label={t('page.liveConsultation.invitationDialog.tab.doctor')} value="doctor" />
              <Tab label={t('page.liveConsultation.invitationDialog.tab.nurse')} value="pds" />
              <Tab label={t('page.liveConsultation.invitationDialog.tab.patient')} value="patient" />
            </Tabs>
          )}
          <Stack spacing="20px">
            <Controller
              render={({ field, fieldState: { isTouched, error } }) => (
                <Autocomplete
                  filterOptions={(options, params) => {
                    const { inputValue } = params
                    return participantTypeTab === 'doctor' &&
                      inputValue !== '' &&
                      !error &&
                      !options.length &&
                      !isSearching &&
                      !participants.find((participant) => participant.email === inputValue)
                      ? [{ creatable: true, label: inputValue, value: inputValue }]
                      : options
                  }}
                  filterSelectedOptions
                  freeSolo
                  fullWidth
                  inputValue={field.value as string}
                  loading={isSearching}
                  onChange={(_event, selectedOption, reason) => {
                    if (!selectedOption || typeof selectedOption === 'string' || reason !== 'selectOption') return

                    if ('creatable' in selectedOption && selectedOption.creatable) {
                      const { value: email } = selectedOption
                      onSelectParticipant?.({
                        email,
                        name: randomString(6),
                        surname: 'MedDoc',
                        type: ConsultationParticipantType.DOCTOR,
                        assignToDoctor: assignDoctor,
                        skipNotification,
                      })
                      return
                    }
                    const selectedParticipant = suggestedParticipants.find((participant) => participant.email === selectedOption.value)
                    if (selectedParticipant) {
                      onSelectParticipant?.({ ...selectedParticipant, assignToDoctor: assignDoctor, skipNotification })
                    }
                  }}
                  onInputChange={(_event, inputValue, reason) => {
                    if (reason === 'clear' || reason === 'reset') setValue('email', inputValue)
                    debouncedOnChange(inputValue)
                  }}
                  options={suggestedParticipantOptions}
                  renderInput={(params) => (
                    <TextInput
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        onChange: (event: React.FocusEvent<HTMLInputElement>) => {
                          params.inputProps.onChange?.(event)
                          field.onChange(event)
                        },
                        onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                          params.inputProps.onBlur?.(event)
                          field.onBlur()
                        },
                      }}
                      name={field.name}
                      placeholder={t('page.liveConsultation.invitationDialog.searchInput.placeholder')}
                      inputRef={field.ref}
                    />
                  )}
                  sx={{
                    '.MuiOutlinedInput-root': {
                      padding: 0,
                    },
                  }}
                />
              )}
              name="email"
              control={control}
            />
            {participantTypeTab === 'doctor' && (
              <FormControlLabel
                control={<Checkbox checked={assignDoctor} onChange={(e) => setAssignDoctor(e.target.checked)} color="primary" />}
                label={t('page.liveConsultation.invitationDialog.assignToDoctor.label')}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={skipNotification}
                  onChange={(e) => {
                    setSkipNotification(e.target.checked)
                    localStorage.setItem('inviteSkipNotification', String(e.target.checked))
                  }}
                  color="primary"
                />
              }
              label={t('page.liveConsultation.invitationDialog.skipNotification.label')}
            />
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
              {participants
                .filter((participant) => participant.type.toLowerCase() === participantTypeTab)
                .map((participant) => (
                  <ListItem
                    key={participant.email}
                    secondaryAction={
                      <Tooltip title={t('component.consultation.remove.tooltip')}>
                        <IconButton onClick={handleRemoveParticipant(participant)}>
                          <PersonRemoveRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={participantIconMap[participantTypeTab]} />
                    </ListItemAvatar>
                    <ListItemText primary={`${participant.name} ${participant.surname}`} secondary={participant.email} />
                  </ListItem>
                ))}
            </List>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default InvitationDialog
